import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { quizAPI } from '../services/api';
import * as mixpanelService from '../services/mixpanelService';
import { BookIcon, ClockIcon, SparkleIcon } from './Icons';
import Modal from './Modal';
import styles from '../styles/Quiz.module.css';

// The linen "floor" behind the fullscreen quiz — the same photo /meet-sophia
// uses, so the quiz doesn't look like a different product when it takes over
// that screen. Lives in client/public/assets and is referenced via PUBLIC_URL
// (a literal url() in the CSS module would make css-loader try to bundle it at
// build time and fail), then handed to the stylesheet as --quiz-bg-image.
const QUIZ_BG_IMAGE = `url(${process.env.PUBLIC_URL}/assets/bracelet-background.webp)`;

/**
 * Quiz - Unified quiz component with multiple presentation variants
 *
 * Variants:
 * - "chat" - Compact card in chat messages (shows quiz info + "Take Quiz" button)
 * - "fullscreen" - Full-screen quiz experience (takes over the screen)
 *
 * Props:
 * - slug: string (required) - Quiz identifier
 * - title: string (required) - Quiz title
 * - description: string (optional) - Quiz description
 * - estimatedMinutes: number (optional) - Estimated completion time
 * - variant: "chat" | "fullscreen" (default: "chat")
 * - onComplete: function (optional) - Called when quiz is completed with results
 * - onClose: function (optional) - Called when quiz is closed/abandoned
 * - isCompleted: boolean (optional) - Whether user has already completed this quiz
 * - previewQuestion: string (optional) - First question to show as teaser
 * - questionCount: number (optional) - Total number of questions
 * - quizData: object (optional) - Pre-fetched quiz definition; when provided the
 *   component makes NO fetch (enables pre-auth use, e.g. /meet-sophia)
 * - onSubmitQuiz: async fn(slug, answers, reflections) → { result } (optional) -
 *   Overrides the authenticated submit endpoint (pre-auth use)
 * - onDownloadResults: fn(results) (optional) - When provided, the results
 *   screen shows a "Download your results (PDF)" secondary button
 */
function Quiz({
  slug,
  title,
  description,
  estimatedMinutes = 5,
  variant = 'chat',
  onComplete,
  onClose,
  isCompleted: initialIsCompleted = false,
  previewQuestion,
  questionCount,
  quizData,
  onSubmitQuiz,
  onDownloadResults
}) {
  const [showFullscreen, setShowFullscreen] = useState(variant === 'fullscreen');
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [reflections, setReflections] = useState({});
  const [showAbandonModal, setShowAbandonModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState(null);
  const [isCompleted, setIsCompleted] = useState(initialIsCompleted);
  const [downloadingResults, setDownloadingResults] = useState(false);

  // Fetch quiz data when fullscreen opens
  const fetchQuiz = useCallback(async () => {
    if (!slug) return;

    setLoading(true);
    setError(null);

    try {
      const response = await quizAPI.getQuiz(slug);
      setQuiz(response.quiz);
      setIsCompleted(!!response.previousResult);
    } catch (err) {
      console.error('[Quiz] Failed to fetch quiz:', err);
      setError('Failed to load quiz. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (showFullscreen && !quiz) {
      // Injected definition (pre-auth use) short-circuits the authed fetch.
      if (quizData) {
        setQuiz(quizData);
        return;
      }
      fetchQuiz();
    }
  }, [showFullscreen, quiz, fetchQuiz, quizData]);

  // Calculate progress
  const getTotalQuestions = () => {
    if (!quiz?.sections) return 0;
    return quiz.sections.reduce((total, section) => total + (section.questions?.length || 0), 0);
  };

  const getCurrentQuestionNumber = () => {
    if (!quiz?.sections) return 0;
    let count = 0;
    for (let i = 0; i < currentSectionIndex; i++) {
      count += quiz.sections[i].questions?.length || 0;
    }
    return count + currentQuestionIndex + 1;
  };

  const progress = getTotalQuestions() > 0 ? (getCurrentQuestionNumber() / getTotalQuestions()) * 100 : 0;

  // Get current section and question
  const currentSection = quiz?.sections?.[currentSectionIndex];
  const currentQuestion = currentSection?.questions?.[currentQuestionIndex];

  // Handle answer selection (clicking same option twice advances to next)
  const handleAnswer = (questionId, value) => {
    if (!currentSection) return;
    const key = `${currentSection.id}.${questionId}`;

    if (currentQuestion?.type === 'text') {
      setReflections(prev => ({ ...prev, [key]: value }));
    } else if (currentQuestion?.type === 'multi_select') {
      setAnswers(prev => {
        const current = prev[key] || [];
        if (current.includes(value)) {
          return { ...prev, [key]: current.filter(v => v !== value) };
        } else {
          return { ...prev, [key]: [...current, value] };
        }
      });
    } else {
      // For single_choice and likert: clicking same option again advances
      const currentAnswer = answers[key];
      if (currentAnswer === value) {
        handleNext();
        return;
      }
      setAnswers(prev => ({ ...prev, [key]: value }));
    }
  };

  // Get current answer value
  const getCurrentAnswer = () => {
    if (!currentSection || !currentQuestion) return null;
    const key = `${currentSection.id}.${currentQuestion.id}`;

    if (currentQuestion.type === 'text') {
      return reflections[key] || '';
    }
    return answers[key];
  };

  // Check if current question is answered
  const isCurrentQuestionAnswered = () => {
    const answer = getCurrentAnswer();
    if (currentQuestion?.type === 'text') {
      return true; // Text is optional
    }
    if (currentQuestion?.type === 'multi_select') {
      return Array.isArray(answer) && answer.length > 0;
    }
    return answer !== null && answer !== undefined;
  };

  // Navigate to next question
  const handleNext = () => {
    if (!quiz?.sections) return;

    const section = quiz.sections[currentSectionIndex];
    if (currentQuestionIndex < section.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    } else if (currentSectionIndex < quiz.sections.length - 1) {
      setCurrentSectionIndex(prev => prev + 1);
      setCurrentQuestionIndex(0);
    } else {
      // Quiz complete - submit
      handleSubmit();
    }
  };

  // Navigate to previous question
  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentSectionIndex > 0) {
      setCurrentSectionIndex(prev => prev - 1);
      const prevSection = quiz.sections[currentSectionIndex - 1];
      setCurrentQuestionIndex(prevSection.questions.length - 1);
    }
  };

  // Submit quiz
  const handleSubmit = async () => {
    if (submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      // Pre-auth callers supply their own submit (public endpoint); default
      // is the authenticated quizAPI path.
      const response = onSubmitQuiz
        ? await onSubmitQuiz(slug, answers, reflections)
        : await quizAPI.submitQuiz(slug, answers, reflections);
      setResults(response.result);
      // Note: onComplete is called when user closes results screen (in handleClose)
    } catch (err) {
      console.error('[Quiz] Failed to submit quiz:', err);
      setError('Failed to submit quiz. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle close/abandon
  const handleClose = () => {
    if (results) {
      // Quiz completed - notify parent with results, then close
      if (onComplete) {
        onComplete(results);
      }
      setShowFullscreen(false);
      setResults(null);
      setAnswers({});
      setReflections({});
      setCurrentSectionIndex(0);
      setCurrentQuestionIndex(0);
      if (onClose) onClose();
    } else if (Object.keys(answers).length > 0 || Object.keys(reflections).length > 0) {
      // Has progress, show confirmation
      setShowAbandonModal(true);
    } else {
      // No progress, safe to close
      setShowFullscreen(false);
      if (onClose) onClose();
    }
  };

  const handleConfirmAbandon = () => {
    setShowAbandonModal(false);
    setShowFullscreen(false);
    setAnswers({});
    setReflections({});
    setCurrentSectionIndex(0);
    setCurrentQuestionIndex(0);
    if (onClose) onClose();
  };

  // Render question based on type
  const renderQuestion = () => {
    if (!currentQuestion) return null;

    switch (currentQuestion.type) {
      case 'single_choice':
        return (
          <div className={styles.choices}>
            {currentQuestion.choices?.map((choice, index) => (
              <button
                key={choice.id}
                className={`${styles.choiceButton} ${getCurrentAnswer() === choice.id ? styles.selected : ''}`}
                style={{ '--i': index }}
                onClick={() => handleAnswer(currentQuestion.id, choice.id)}
              >
                {choice.text}
              </button>
            ))}
          </div>
        );

      case 'likert':
      case 'likert_no_emoji':
        const scale = currentQuestion.scale || { min: 1, max: 5 };
        const values = [];
        for (let i = scale.min; i <= scale.max; i++) {
          values.push(i);
        }
        const showEmoji = currentQuestion.type === 'likert';
        // Multiple emoji sets - rotate based on question index
        const emojiSets = [
          ['😟', '😕', '😐', '🙂', '😊'],  // Faces
          ['👎', '🤔', '🤷', '👍', '💪'],  // Thumbs/gestures
          ['😰', '😬', '😌', '😄', '🤩']  // Expressions
        ];
        const emojiSetIndex = currentQuestionIndex % emojiSets.length;
        const emojis = emojiSets[emojiSetIndex];
        const currentAnswer = getCurrentAnswer();
        return (
          <div className={styles.likertScale}>
            <div className={styles.likertTrack}>
              <div
                className={styles.likertFill}
                style={{ width: currentAnswer ? `${((currentAnswer - scale.min) / (scale.max - scale.min)) * 100}%` : '0%' }}
              />
            </div>
            <div className={styles.likertOptions}>
              {values.map((value, index) => (
                <button
                  key={value}
                  className={`${styles.likertOption} ${currentAnswer === value ? styles.selected : ''} ${currentAnswer && currentAnswer >= value ? styles.filled : ''}`}
                  style={{ '--i': index }}
                  onClick={() => handleAnswer(currentQuestion.id, value)}
                >
                  {showEmoji && <span className={styles.likertEmoji}>{emojis[index] || value}</span>}
                  <span className={styles.likertValue}>{value}</span>
                </button>
              ))}
            </div>
            <div className={styles.likertLabels}>
              <span>{scale.minLabel || 'Strongly Disagree'}</span>
              <span>{scale.maxLabel || 'Strongly Agree'}</span>
            </div>
          </div>
        );

      case 'multi_select':
        const selectedValues = getCurrentAnswer() || [];
        return (
          <div className={styles.choices}>
            {currentQuestion.choices?.map((choice, index) => (
              <button
                key={choice.id}
                className={`${styles.choiceButton} ${styles.multiSelect} ${selectedValues.includes(choice.id) ? styles.selected : ''}`}
                style={{ '--i': index }}
                onClick={() => handleAnswer(currentQuestion.id, choice.id)}
              >
                <span className={styles.checkbox}>
                  {selectedValues.includes(choice.id) ? '☑' : '☐'}
                </span>
                {choice.text}
              </button>
            ))}
          </div>
        );

      case 'text':
        return (
          <div className={styles.textInput}>
            <textarea
              className={styles.textarea}
              placeholder={currentQuestion.placeholder || 'Share your thoughts...'}
              value={getCurrentAnswer() || ''}
              onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
              maxLength={currentQuestion.maxLength || 500}
            />
            <div className={styles.charCount}>
              {(getCurrentAnswer() || '').length} / {currentQuestion.maxLength || 500}
            </div>
          </div>
        );

      default:
        return <div>Unknown question type</div>;
    }
  };

  // Render results screen
  const renderResults = () => {
    if (!results) return null;

    return (
      <div className={styles.resultsScreen}>
        <div className={styles.resultsHeader}>
          <div className={styles.celebrationMark}><SparkleIcon size={52} /></div>
          <h2 className={styles.resultsTitle}>Quiz Complete!</h2>
        </div>

        <div className={styles.resultsList}>
          {results.sectionResults?.map((result, index) => (
            <div key={index} className={styles.resultCard} style={{ '--i': index }}>
              <h3 className={styles.resultSectionTitle}>{result.sectionTitle}</h3>
              <div className={styles.resultType}>{result.resultTitle}</div>
              {result.resultDescription && (
                <p className={styles.resultDescription}>{result.resultDescription}</p>
              )}
              {result.score !== undefined && result.maxScore && (
                <div className={styles.resultScore}>
                  Score: {result.score} / {result.maxScore}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className={styles.resultsActions}>
          {onDownloadResults && (
            <button
              className={styles.downloadResultsButton}
              disabled={downloadingResults}
              onClick={async () => {
                if (downloadingResults) return;
                setDownloadingResults(true);
                try {
                  await onDownloadResults(results);
                } finally {
                  setDownloadingResults(false);
                }
              }}
            >
              {downloadingResults ? (
                <>
                  <span className={styles.downloadSpinner} aria-hidden="true" />
                  Preparing your PDF…
                </>
              ) : (
                <>
                  <span className={styles.downloadIcon} aria-hidden="true">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 3v12M7 10l5 5 5-5M4 20h16"
                        stroke="currentColor"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  Download your results (PDF)
                </>
              )}
            </button>
          )}

          {/* When a download is offered it's the primary action, so Continue
              steps down to a secondary (outline) treatment. */}
          <button
            className={`${styles.closeResultsButton} ${onDownloadResults ? styles.closeResultsSecondary : ''}`}
            onClick={handleClose}
          >
            Continue
          </button>
        </div>
      </div>
    );
  };

  // Chat variant - compact card
  if (variant === 'chat' && !showFullscreen) {
    return (
      <div className={styles.quizCard}>
        <div className={styles.quizCardIcon}><SparkleIcon size={30} /></div>
        <div className={styles.quizCardHeader}>
          <span className={styles.quizBadge}>Quiz</span>
          {isCompleted && <span className={styles.completedBadge}>Completed</span>}
        </div>
        <h3 className={styles.quizCardTitle}>{title}</h3>
        {description && <p className={styles.quizCardDescription}>{description}</p>}

        {/* Preview question teaser */}
        {previewQuestion && !isCompleted && (
          <div className={styles.previewQuestion}>
            <div className={styles.previewLabel}>First question:</div>
            <p className={styles.previewText}>"{previewQuestion}"</p>
          </div>
        )}

        <div className={styles.quizCardMeta}>
          <span className={styles.quizCardTime}><ClockIcon size={14} /> {estimatedMinutes} min</span>
          {questionCount && <span className={styles.quizCardQuestions}><BookIcon size={14} /> {questionCount} questions</span>}
        </div>
        <button
          className={styles.takeQuizButton}
          onClick={() => {
            // Track quiz started event
            mixpanelService.trackEvent('Quiz Started', {
              quiz_slug: slug,
              quiz_title: title,
              is_retake: isCompleted,
              question_count: questionCount,
              estimated_minutes: estimatedMinutes
            });
            setShowFullscreen(true);
          }}
        >
          {isCompleted ? 'Retake Quiz' : 'Start Quiz →'}
        </button>

        {/* Render fullscreen if opened from chat card */}
        {showFullscreen && (
          <Quiz
            slug={slug}
            title={title}
            description={description}
            estimatedMinutes={estimatedMinutes}
            variant="fullscreen"
            onComplete={(result) => {
              setShowFullscreen(false);
              setIsCompleted(true);
              if (onComplete) onComplete(result);
            }}
            onClose={() => setShowFullscreen(false)}
          />
        )}
      </div>
    );
  }

  // Fullscreen variant - use portal to escape parent stacking contexts
  return createPortal(
    <div className={styles.fullscreenQuiz} style={{ '--quiz-bg-image': QUIZ_BG_IMAGE }}>
      {/* Decorative linen floor, fading up into the teal wash */}
      <div className={styles.floor} aria-hidden="true" />

      {/* Header with close button */}
      <div className={styles.fullscreenHeader}>
        <button className={styles.closeButton} onClick={handleClose} aria-label="Close quiz">
          ✕
        </button>
        <div className={styles.headerInner}>
          <div className={styles.quizTitle}>{title || quiz?.title}</div>
          {!results && (
            <>
              <div className={styles.progressContainer}>
                <div className={styles.progressBar} style={{ width: `${progress}%` }} />
              </div>
              <div className={styles.progressText}>
                Question {getCurrentQuestionNumber()} of {getTotalQuestions()}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className={styles.fullscreenContent}>
        {loading && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Loading quiz...</p>
          </div>
        )}

        {submitting && (
          <div className={styles.loadingState}>
            <div className={styles.spinner} />
            <p>Analyzing your responses...</p>
          </div>
        )}

        {error && (
          <div className={styles.errorState}>
            <p>{error}</p>
            <button onClick={fetchQuiz}>Try Again</button>
          </div>
        )}

        {results && renderResults()}

        {!loading && !submitting && !error && !results && quiz && currentQuestion && (
          <div
            key={`${currentSectionIndex}-${currentQuestionIndex}`}
            className={styles.questionContainer}
          >
            <div className={styles.sectionTitle}>{currentSection?.title}</div>
            <h2 className={styles.questionText}>{currentQuestion.question}</h2>
            {renderQuestion()}
          </div>
        )}
      </div>

      {/* Navigation */}
      {!loading && !submitting && !error && !results && quiz && (
        <div className={styles.fullscreenNav}>
          {/* Hint for tap-again behavior */}
          {isCurrentQuestionAnswered() && currentQuestion?.type !== 'text' && currentQuestion?.type !== 'multi_select' && (
            <div className={styles.navHint}>Tap your choice again to continue</div>
          )}
          <div className={styles.navButtons}>
            <button
              className={styles.navButton}
              onClick={handlePrevious}
              disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}
            >
              ← Back
            </button>
            <button
              className={`${styles.navButton} ${styles.primary}`}
              onClick={handleNext}
              disabled={!isCurrentQuestionAnswered() || submitting}
            >
              {submitting ? 'Submitting...' :
               (currentSectionIndex === quiz.sections.length - 1 &&
                currentQuestionIndex === currentSection.questions.length - 1)
               ? 'Finish' : 'Next →'}
            </button>
          </div>
        </div>
      )}

      {/* Abandon confirmation modal — shell concerns (backdrop blur, panel,
          focus trap, Escape) come from Modal. Dismissing (Escape/backdrop)
          means "keep going", so onClose continues the quiz; no ✕ because the
          explicit choice buttons are the exits. */}
      <Modal
        isOpen={showAbandonModal}
        onClose={() => setShowAbandonModal(false)}
        labelledBy="quiz-abandon-title"
        size="sm"
        showClose={false}
      >
        <div className={styles.abandonContent}>
          <h3 id="quiz-abandon-title">Leave Quiz?</h3>
          <p>Are you sure you want to leave? Your progress won't be saved.</p>
          <div className={styles.modalButtons}>
            <button
              className={styles.cancelButton}
              onClick={() => setShowAbandonModal(false)}
            >
              Continue Quiz
            </button>
            <button
              className={styles.confirmButton}
              onClick={handleConfirmAbandon}
            >
              Leave
            </button>
          </div>
        </div>
      </Modal>
    </div>,
    document.body
  );
}

export default Quiz;
